/**
 * services/api/index.ts - API 服务统一导出入口
 *
 * 功能说明：
 * - 统一导出 API 相关功能
 * - 保持向后兼容，已有的导入路径继续有效
 *
 * 依赖关系：
 * - 依赖 api/config.ts 的 axios 实例
 * - 依赖 api/interceptors.ts 的拦截器
 * - 依赖 api/sync.ts 的离线同步
 * - 依赖 api/offline.ts 的离线检测
 *
 * 关键注释索引：
 * - [导出配置] - 见下方导出语句
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 * 重构日期：2026-05-03 - 按功能拆分为多个文件
 */

// 导出 axios 实例和增强 API
export { api } from './config';
export { default } from './config';

// 导出拦截器和工具函数
export {
  installFetchPermissionSync,
  redirectToLogin,
} from './interceptors';

// 导出离线同步功能
export {
  offlinePost,
  offlinePut,
  offlineDelete,
  getDataTypeFromUrl,
  getIdFromUrl,
  isOnline as checkOnline,
  type DataType,
} from './sync';

// 导出离线检测和重试
export {
  isBrowser,
  retryRequest,
  setupOfflineListener,
  type RetryConfig,
} from './offline';

// ==================== 增强的 API 方法 ====================

import { api } from './config';
import { syncService } from '../syncService';

/**
 * [网络检测] 检测网络是否在线
 */
const isOnline = (): boolean => {
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    return navigator.onLine;
  }
  return true;
};

/**
 * [数据类型映射] 从 URL 提取数据类型
 */
const getDataTypeFromUrl = (url: string): DataType | null => {
  if (url.includes('/worktimes')) return 'worktime';
  if (url.includes('/equipment-usage')) return 'equipment';
  if (url.includes('/material-usage')) return 'material';
  if (url.includes('/projects')) return 'project';
  if (url.includes('/price-config')) return 'priceconfig';
  return null;
};

/**
 * [ID提取] 从 URL 提取资源 ID
 */
const getIdFromUrl = (url: string): string | null => {
  const matches = url.match(/\/([a-f\d]{24})$/);
  return matches ? matches[1] : null;
};

/**
 * [增强API] 支持离线缓存的 API 方法集合
 */
export const enhancedApi = {
  /** 原始 API 实例 */
  raw: api,

  /** 支持离线缓存的 POST */
  async post(url: string, data: any, options?: { cache?: boolean }) {
    const shouldCache = options?.cache !== false;
    try {
      return await api.post(url, data);
    } catch (error) {
      if (!isOnline() && shouldCache) {
        const type = getDataTypeFromUrl(url);
        if (type) {
          syncService.cacheData({ type, action: 'create', data });
          console.log(`数据已缓存，将在网络恢复后同步: ${type}`);
          return { success: true, cached: true };
        }
      }
      throw error;
    }
  },

  /** 支持离线缓存的 PUT */
  async put(url: string, data: any, options?: { cache?: boolean }) {
    const shouldCache = options?.cache !== false;
    try {
      return await api.put(url, data);
    } catch (error) {
      if (!isOnline() && shouldCache) {
        const type = getDataTypeFromUrl(url);
        if (type) {
          syncService.cacheData({ type, action: 'update', data });
          console.log(`数据已缓存，将在网络恢复后同步: ${type}`);
          return { success: true, cached: true };
        }
      }
      throw error;
    }
  },

  /** 支持离线缓存的 DELETE */
  async delete(url: string, options?: { cache?: boolean }) {
    const shouldCache = options?.cache !== false;
    try {
      return await api.delete(url);
    } catch (error) {
      if (!isOnline() && shouldCache) {
        const type = getDataTypeFromUrl(url);
        const id = getIdFromUrl(url);
        if (type && id) {
          syncService.cacheData({ type, action: 'delete', data: { _id: id } });
          console.log(`删除操作已缓存，将在网络恢复后同步: ${type}`);
          return { success: true, cached: true };
        }
      }
      throw error;
    }
  },

  /** GET 请求（不缓存） */
  async get(url: string, config?: any) {
    return api.get(url, config);
  },

  /** 从 URL 获取数据类型 */
  getDataTypeFromUrl,

  /** 从 URL 获取 ID */
  getIdFromUrl,
};

// ==================== authFetch ====================

/**
 * [获取本地Token] 从 localStorage 获取认证 token
 */
const getAuthStorageRaw = (): string | null => {
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    return localStorage.getItem('auth-storage');
  }
  return null;
};

/**
 * [获取Token] 从存储中提取 token
 */
const getTokenFromStorage = (): string | null => {
  try {
    const authStorage = getAuthStorageRaw();
    if (!authStorage) return null;
    const authState = JSON.parse(authStorage);
    return authState?.state?.token || authState?.token || null;
  } catch {
    return null;
  }
};

/**
 * [认证Fetch] 带认证的 fetch 封装
 *
 * @description 自动添加 Authorization header
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getTokenFromStorage();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return window.fetch(url, { ...options, headers });
};
